/**
 * M-03 "Stok arama & ürün kartı" — the pure half: which item types a filter chip
 * covers, which rows a typed term matches and *why* it matched, the order the
 * results come back in, and how the item card's "Lokasyona göre" rows are built
 * out of the two views behind them.
 *
 * It works on rows the views already returned (`v_stock_item_summary`,
 * `v_item_stock_by_location`, `v_stock_lots`), so the phone searches its
 * persisted query cache offline with exactly the logic the server-backed list
 * uses online, and `packages/core/test/stock-search.test.ts` pins it against the
 * rows of the artboard.
 *
 * Quantities stay in base units until the last step; the division by the stock
 * unit's factor happens once, through `matrixQty`, which is the same helper W-06
 * uses — so a napkin reads "1.060 koli" on both the phone and the browser.
 */
import { D, type Dec } from '../money';
import { ITEM_TYPE_ORDER, MARKETPLACE_ORDER, type ItemType, type WarehouseType } from '../constants';
import { searchKey } from '../text';
import { matrixQty, sortLotsFefo } from './matrix';
import type { StockStatus } from './status';

// ── filter chips (§2.3) ─────────────────────────────────────────────────────
export const STOCK_TYPE_FILTERS = ['all', 'finished', 'raw', 'packaging'] as const;
export type StockTypeFilter = (typeof STOCK_TYPE_FILTERS)[number];

/**
 * Chip → the `items.type` values behind it. "Tümü" is every stock type, which is
 * every type except `service`: a service has no quantity, and `v_stock_item_summary`
 * already leaves it out — the list must not depend on that twice.
 */
export const STOCK_FILTER_TYPES: Record<StockTypeFilter, ItemType[]> = {
  all: ITEM_TYPE_ORDER.filter((t) => t !== 'service'),
  finished: ['finished_good', 'semi_finished'],
  raw: ['raw_material', 'consumable'],
  packaging: ['packaging'],
};

/** The stored chip (AsyncStorage, a route param): anything unknown reads "Tümü". */
export function parseStockTypeFilter(raw: string | null | undefined): StockTypeFilter {
  const value = (raw ?? '').trim() as StockTypeFilter;
  return (STOCK_TYPE_FILTERS as readonly string[]).includes(value) ? value : 'all';
}

export function stockFilterMatches(filter: StockTypeFilter, type: ItemType | null | undefined): boolean {
  if (filter === 'all') return !type || type !== 'service';
  return !!type && STOCK_FILTER_TYPES[filter].includes(type);
}

// ── rows and matching ───────────────────────────────────────────────────────
/** The `v_stock_item_summary` columns the search and the row need. */
export interface StockSearchRow {
  item_id: string | null;
  sku?: string | null;
  name_tr?: string | null;
  name_en?: string | null;
  short_name?: string | null;
  type?: ItemType | null;
  barcode?: string | null;
  stock_unit_factor?: number | null;
  /** Every warehouse, FBA included — what the artboard's 1.060 is. */
  total_qty_base?: number | null;
  own_qty_base?: number | null;
  stock_status?: string | null;
  lot_nos?: string[] | null;
  location_codes?: string[] | null;
}

/** Why a row is in the result: the second line of the M-03 row says so. */
export type StockMatchKind = 'none' | 'sku' | 'barcode' | 'name' | 'lot' | 'location';

export interface StockMatch {
  kind: StockMatchKind;
  /** The lot number or location code that matched; null for a name/sku hit. */
  value: string | null;
  /** Lower sorts first: exactness first, then code before name (§2.4 "Sort"). */
  rank: number;
}

export interface StockSearchHit<T extends StockSearchRow> {
  row: T;
  itemId: string;
  match: StockMatch;
  status: StockStatus;
  /** Total on hand in the item's stock unit — the figure the row prints. */
  qtyDisplay: Dec;
}

/** `v_stock_item_summary.stock_status` is the authority; anything else reads normal. */
export function rowStockStatus(row: { stock_status?: string | null }): StockStatus {
  const s = row.stock_status;
  return s === 'low' || s === 'critical' || s === 'negative' ? s : 'normal';
}

/** Total on hand (own + FBA) in the stock unit — `matrixQty`, so W-06 and M-03 agree. */
export function rowQtyDisplay(row: StockSearchRow): Dec {
  return matrixQty(row.total_qty_base ?? 0, row.stock_unit_factor);
}

const KIND_RANK: Record<Exclude<StockMatchKind, 'none'>, number> = { sku: 0, barcode: 1, lot: 2, location: 3, name: 4 };

/** 0 exact · 1 starts with · 2 contains · null no match. All folded (`searchKey`). */
function tier(hay: string | null | undefined, needle: string): 0 | 1 | 2 | null {
  const h = searchKey(hay ?? '');
  if (!h) return null;
  if (h === needle) return 0;
  if (h.startsWith(needle)) return 1;
  return h.includes(needle) ? 2 : null;
}

function best(current: StockMatch | null, kind: Exclude<StockMatchKind, 'none'>, value: string | null, t: 0 | 1 | 2 | null): StockMatch | null {
  if (t === null) return current;
  const rank = t * 10 + KIND_RANK[kind];
  return current && current.rank <= rank ? current : { kind, value, rank };
}

/**
 * Why this row matches the term, and how strongly. An exact code beats every
 * prefix (an operator who typed a whole SKU means that SKU), and inside one tier
 * a code beats a name — which is what puts "NAP-4040-18-FLD" above the item whose
 * description merely mentions it.
 *
 * The haystack is the one §2.2 names: name (tr + en + short), SKU, barcode, and
 * the lot numbers and location codes the summary view already carries, so a lot
 * or a shelf can be searched without a second query.
 */
export function classifyStockMatch(row: StockSearchRow, query: string): StockMatch | null {
  const needle = searchKey(query.trim());
  if (!needle) return { kind: 'none', value: null, rank: 0 };
  let match: StockMatch | null = null;
  match = best(match, 'sku', null, tier(row.sku, needle));
  match = best(match, 'barcode', null, tier(row.barcode, needle));
  match = best(match, 'name', null, tier(row.name_tr, needle));
  match = best(match, 'name', null, tier(row.name_en, needle));
  match = best(match, 'name', null, tier(row.short_name, needle));
  for (const lot of row.lot_nos ?? []) match = best(match, 'lot', lot, tier(lot, needle));
  for (const code of row.location_codes ?? []) match = best(match, 'location', code, tier(code, needle));
  return match;
}

const STATUS_RANK: Record<StockStatus, number> = { negative: 0, critical: 1, low: 2, normal: 3 };

const byName = (a: StockSearchRow, b: StockSearchRow): number =>
  (a.name_tr ?? '').localeCompare(b.name_tr ?? '', 'tr') || (a.sku ?? '').localeCompare(b.sku ?? '', 'tr');

export interface StockSearchOptions {
  query?: string | null;
  filter?: StockTypeFilter;
  /** Narrows the list to the items with stock in this location (a scanned shelf, §2.8). */
  locationCode?: string | null;
}

/**
 * The M-03 result list. With a term: the matches, strongest first. Without one:
 * what needs attention first (negatif → kritik → düşük → the rest), then by name
 * — an operator who opens the tab without typing is looking for a problem, not
 * for the alphabet.
 */
export function searchStockItems<T extends StockSearchRow>(rows: T[], opts: StockSearchOptions = {}): StockSearchHit<T>[] {
  const filter = opts.filter ?? 'all';
  const query = (opts.query ?? '').trim();
  const locationKey = opts.locationCode ? searchKey(opts.locationCode) : null;
  const hits: StockSearchHit<T>[] = [];

  for (const row of rows) {
    if (!row.item_id) continue;
    if (!stockFilterMatches(filter, row.type)) continue;
    if (locationKey && !(row.location_codes ?? []).some((c) => searchKey(c) === locationKey)) continue;
    const match = classifyStockMatch(row, query);
    if (!match) continue;
    hits.push({ row, itemId: row.item_id, match, status: rowStockStatus(row), qtyDisplay: rowQtyDisplay(row) });
  }

  return hits.sort((a, b) =>
    query ? a.match.rank - b.match.rank || byName(a.row, b.row) : STATUS_RANK[a.status] - STATUS_RANK[b.status] || byName(a.row, b.row),
  );
}

// ── the card's "Lokasyona göre" rows (§2.5) ─────────────────────────────────
/** `v_item_stock_by_location` — item × location, lots summed. */
export interface StockCardLocationInput {
  warehouse_id?: string | null;
  warehouse_code?: string | null;
  warehouse_type?: WarehouseType | null;
  marketplace_id?: string | null;
  location_id?: string | null;
  location_code?: string | null;
  qty_base?: number | null;
  updated_at?: string | null;
}

/** `v_stock_lots` — lot × location, which is the grain the card row draws. */
export interface StockCardLotInput {
  lot_id?: string | null;
  lot_no?: string | null;
  location_id?: string | null;
  location_code?: string | null;
  warehouse_id?: string | null;
  warehouse_type?: WarehouseType | null;
  qty_base?: number | null;
  expiry_date?: string | null;
  received_at?: string | null;
  is_blocked?: boolean | null;
}

export interface StockCardRow {
  /** Stable list key; also what a selected row is remembered by. */
  key: string;
  /** The location code, or the marketplace of an FBA warehouse ("US"). */
  code: string;
  isFba: boolean;
  warehouseId: string | null;
  locationId: string | null;
  locationCode: string | null;
  lotId: string | null;
  lotNo: string | null;
  isBlocked: boolean;
  qtyBase: Dec;
  /** In the item's stock unit — the number the row prints. */
  qtyDisplay: Dec;
}

const fbaRank = (code: string): number => {
  const i = (MARKETPLACE_ORDER as readonly string[]).indexOf(code);
  return i < 0 ? MARKETPLACE_ORDER.length : i;
};

/**
 * One row per location × lot, in the order the card lists them: own warehouses by
 * location code, then the FBA rows in `MARKETPLACE_ORDER` — which is what makes
 * the artboard's four own rows (962) add up to the 1.060 of the "Eldeki" tile.
 *
 * A lot-tracked item takes its rows from the lot view in FEFO order, and what the
 * location holds beyond its known lots keeps a row of its own with "—" instead of
 * being silently dropped: an unexplained 40 koli on a shelf is exactly what a
 * count is for. An item that is not lot-tracked (or a location whose lots are not
 * in the cache) gets one row per location, which is also the shape a company with
 * a single default location sees (MISSING_INPUTS E1).
 */
export function stockCardRows(input: {
  locations: StockCardLocationInput[];
  lots?: StockCardLotInput[] | null;
  /** `v_stock_item_summary.stock_unit_factor`; a missing factor means 1:1. */
  factor?: number | null;
}): StockCardRow[] {
  const factor = input.factor;
  const lotsByLocation = new Map<string, StockCardLotInput[]>();
  for (const lot of input.lots ?? []) {
    if (D(lot.qty_base ?? 0).isZero()) continue;
    const key = lot.location_id ?? lot.location_code ?? '';
    const list = lotsByLocation.get(key) ?? [];
    list.push(lot);
    lotsByLocation.set(key, list);
  }

  // the FEFO order inside a location has to survive the outer sort, so each row
  // carries the position it was emitted at
  const rows: { row: StockCardRow; seq: number }[] = [];
  let seq = 0;
  for (const loc of input.locations) {
    const qtyBase = D(loc.qty_base ?? 0);
    if (qtyBase.isZero()) continue;
    const isFba = loc.warehouse_type === 'fba';
    const code = (isFba ? loc.marketplace_id || loc.warehouse_code : loc.location_code) ?? loc.warehouse_code ?? '';
    const locationId = loc.location_id ?? null;
    const base = {
      code,
      isFba,
      warehouseId: loc.warehouse_id ?? null,
      locationId,
      locationCode: loc.location_code ?? null,
    };
    const lots = sortLotsFefo(lotsByLocation.get(locationId ?? loc.location_code ?? '') ?? []);
    let covered = D(0);
    for (const lot of lots) {
      const lotQty = D(lot.qty_base ?? 0);
      covered = covered.plus(lotQty);
      rows.push({
        seq: seq++,
        row: {
          ...base,
          key: `${locationId ?? code}:${lot.lot_id ?? lot.lot_no ?? ''}`,
          lotId: lot.lot_id ?? null,
          lotNo: lot.lot_no ?? null,
          isBlocked: !!lot.is_blocked,
          qtyBase: lotQty,
          qtyDisplay: matrixQty(lotQty, factor),
        },
      });
    }
    const rest = qtyBase.minus(covered);
    if (!rest.isZero()) {
      rows.push({
        seq: seq++,
        row: {
          ...base,
          key: `${locationId ?? code}:-`,
          lotId: null,
          lotNo: null,
          isBlocked: false,
          qtyBase: rest,
          qtyDisplay: matrixQty(rest, factor),
        },
      });
    }
  }

  return rows
    .sort(
      (a, b) =>
        Number(a.row.isFba) - Number(b.row.isFba) ||
        (a.row.isFba ? fbaRank(a.row.code) - fbaRank(b.row.code) : 0) ||
        a.row.code.localeCompare(b.row.code, 'tr') ||
        a.seq - b.seq,
    )
    .map((r) => r.row);
}

export interface StockCardLot {
  lotId: string;
  lotNo: string;
  expiryDate: string | null;
  receivedAt: string | null;
  isBlocked: boolean;
  /** The shelves this lot is on, in code order — one lot can sit in two places. */
  locationCodes: string[];
  qtyBase: Dec;
  qtyDisplay: Dec;
}

/**
 * The card's "Lotlar" block: one row per lot with what is left of it, **FEFO
 * first** — nearest expiry, then the oldest receipt. That is the order the goods
 * should leave in, so it is the order the operator reads them in; the same
 * `sortLotsFefo` orders the W-07 lot picker, so the phone and the browser never
 * suggest different pallets.
 */
export function stockCardLots(rows: StockCardLotInput[] | null | undefined, factor?: number | null): StockCardLot[] {
  /** The accumulator keeps the view's own column names, so `sortLotsFefo` reads it directly. */
  interface Acc extends StockCardLot {
    expiry_date: string | null;
    received_at: string | null;
    lot_no: string;
  }
  const byLot = new Map<string, Acc>();
  for (const row of rows ?? []) {
    const id = row.lot_id ?? row.lot_no;
    if (!id) continue;
    const qty = D(row.qty_base ?? 0);
    if (qty.isZero()) continue;
    const existing = byLot.get(id);
    if (existing) {
      existing.qtyBase = existing.qtyBase.plus(qty);
      existing.isBlocked = existing.isBlocked || !!row.is_blocked;
      if (row.location_code && !existing.locationCodes.includes(row.location_code)) existing.locationCodes.push(row.location_code);
      continue;
    }
    byLot.set(id, {
      lotId: String(row.lot_id ?? id),
      lotNo: row.lot_no ?? '',
      lot_no: row.lot_no ?? '',
      expiryDate: row.expiry_date ?? null,
      expiry_date: row.expiry_date ?? null,
      receivedAt: row.received_at ?? null,
      received_at: row.received_at ?? null,
      isBlocked: !!row.is_blocked,
      locationCodes: row.location_code ? [row.location_code] : [],
      qtyBase: qty,
      qtyDisplay: D(0),
    });
  }
  return sortLotsFefo([...byLot.values()]).map(({ expiry_date: _e, received_at: _r, lot_no: _l, ...lot }) => ({
    ...lot,
    locationCodes: [...lot.locationCodes].sort((a, b) => a.localeCompare(b, 'tr')),
    qtyDisplay: matrixQty(lot.qtyBase, factor),
  }));
}

/**
 * The card's three tiles. `locationCount` / `lotCount` come from the summary view
 * (distinct rows with a non-zero balance), because the cached location rows may
 * be older than the totals — and a tile that contradicts the list under it is
 * worse than a tile that is simply one refresh behind.
 */
export interface StockCardStats {
  onHandDisplay: Dec;
  locationCount: number;
  lotCount: number;
  isLotTracked: boolean;
}

export function stockCardStats(row: {
  total_qty_base?: number | null;
  stock_unit_factor?: number | null;
  location_count?: number | null;
  lot_count?: number | null;
  is_lot_tracked?: boolean | null;
}): StockCardStats {
  return {
    onHandDisplay: matrixQty(row.total_qty_base ?? 0, row.stock_unit_factor),
    locationCount: Math.max(0, Math.trunc(Number(row.location_count ?? 0))),
    lotCount: Math.max(0, Math.trunc(Number(row.lot_count ?? 0))),
    isLotTracked: !!row.is_lot_tracked,
  };
}
